import { Component, ComponentProps, ErrorInfo } from 'react'
import React from 'react'

import { BugsnagErrorBoundary } from '@bugsnag/plugin-react'

import logger from '@cash-web/shared/util-logger'

import ErrorFallbackComponent from './ErrorFallbackComponent/ErrorFallbackComponent'

const initialState: { hasError: false; error: Error; info: ErrorInfo } = {
  hasError: false,
  error: new Error(),
  info: { componentStack: '' },
}

type Props = ComponentProps<BugsnagErrorBoundary>
type PropsAndErrorBoundary = Props & { MaybeBugSnagErrorBoundary: BugsnagErrorBoundary | undefined }

class ErrorBoundaryInner extends Component<PropsAndErrorBoundary, typeof initialState> {
  constructor(props: PropsAndErrorBoundary) {
    super(props)
    this.state = initialState
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true }
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    this.setState({ ...this.state, error, info })
  }

  clearError = () => {
    this.setState(initialState)
  }

  override render() {
    const FallbackComponent = this.props.FallbackComponent ?? ErrorFallbackComponent
    const { MaybeBugSnagErrorBoundary } = this.props

    const children =
      MaybeBugSnagErrorBoundary !== undefined ? (
        <MaybeBugSnagErrorBoundary {...this.props} FallbackComponent={FallbackComponent} />
      ) : (
        this.props.children
      )

    return this.state.hasError ? (
      <FallbackComponent error={this.state.error} info={this.state.info} clearError={this.clearError} />
    ) : (
      children
    )
  }
}

export const ErrorBoundary = (props: Props) => {
  const MaybeBugSnagErrorBoundary = React.useMemo(() => logger.createErrorBoundary(), [])

  return <ErrorBoundaryInner MaybeBugSnagErrorBoundary={MaybeBugSnagErrorBoundary} {...props} />
}
